import React, { useEffect, useMemo, useState } from 'react';
import logo from '../../assets/logo.svg';
import Divider from '../../components/divider';
import EmailInput from '../../components/emailLogin';
import { PageContent } from '../../components/modal/styles';
import PasskeyButtons from '../../components/passkeyButtons';
import PhoneLogin from '../../components/phoneLogin';
import SocialLogin from '../../components/socialLogin';
import Wallets from '../../components/wallets';
import { useContext } from '../../context';
import { useAppearance } from '../../hooks/useAppearance';
import useIconColor from '../../hooks/useIconColor';
import useLocales from '../../hooks/useLocales';
import { ConnectorType, type ConnectorTypeValue } from '../../types';
import type { AuthWalletConnectorProperties } from '../../types/properties';
import { isSocialAuthType } from '../../utils/social';
import { Logo, SwitchEmailOrPhoneContainer } from './styles';

const fillConnectorsOrder = (enableConnector: boolean, connectorType: ConnectorType, orders: ConnectorTypeValue[]) => {
  if (enableConnector) {
    if (!orders.includes(connectorType)) {
      orders.push(connectorType);
    }
  } else {
    const indexToRemove = orders.indexOf(connectorType);
    if (indexToRemove > -1) {
      orders.splice(indexToRemove, 1);
    }
  }
};

const SwitchEmailOrPhone = ({
  toggleEmailOrPhone,
  setToggleEmailOrPhone,
}: {
  toggleEmailOrPhone: ConnectorTypeValue;
  setToggleEmailOrPhone: React.Dispatch<React.SetStateAction<ConnectorTypeValue>>;
}) => {
  const locales = useLocales();

  return (
    <SwitchEmailOrPhoneContainer>
      <span>{toggleEmailOrPhone === ConnectorType.Phone ? locales.preferEmail : locales.preferPhone}</span>
      <span
        onClick={() => {
          setToggleEmailOrPhone((prevState) => {
            return prevState === ConnectorType.Email ? ConnectorType.Phone : ConnectorType.Email;
          });
        }}
      >
        {toggleEmailOrPhone === ConnectorType.Phone ? locales.useEmail : locales.usePhone}
      </span>
    </SwitchEmailOrPhoneContainer>
  );
};

const Signup = () => {
  const { config } = useContext();
  const [toggleEmailOrPhone, setToggleEmailOrPhone] = useState<ConnectorTypeValue>(ConnectorType.Email);
  const locales = useLocales();
  const { appearance } = useAppearance();
  const { currentMode } = useIconColor();

  const connectorsOrder = useMemo(() => {
    let orders = appearance?.connectorsOrder;
    if (!orders) {
      // default order
      orders = [
        ConnectorType.Email,
        ConnectorType.Phone,
        ConnectorType.Social,
        ConnectorType.Wallet,
        ConnectorType.Passkey,
      ];
    }

    const authTypes = config.getWalletConnector<AuthWalletConnectorProperties>('particleAuth')?.supportAuthTypes || [];
    const enableEmail = authTypes.includes('email');
    const enablePhone = authTypes.includes('phone');
    const enableSocial = authTypes.filter((item) => isSocialAuthType(item)).length > 0;

    const evmWalletConnector = config.getWalletConnector('evmWallet');
    const solanaWalletConnector = config.getWalletConnector('solanaWallet');
    const hasPasskeyConnector = evmWalletConnector?.connectors.some(
      (connector) => connector.id === 'passkeySmartWallet'
    );

    const enableWallet = Boolean(
      (evmWalletConnector && evmWalletConnector.connectors.length > (hasPasskeyConnector ? 1 : 0)) ||
        solanaWalletConnector
    );
    const enablePasskey =
      Boolean(config.plugins.some((plugin) => plugin.id === 'aa') && hasPasskeyConnector) &&
      appearance.collapsePasskeyButton !== 'as-social';

    fillConnectorsOrder(enableEmail, ConnectorType.Email, orders);
    fillConnectorsOrder(enablePhone, ConnectorType.Phone, orders);
    fillConnectorsOrder(enableSocial, ConnectorType.Social, orders);
    fillConnectorsOrder(enableWallet, ConnectorType.Wallet, orders);
    fillConnectorsOrder(enablePasskey, ConnectorType.Passkey, orders);

    return orders;
  }, [appearance?.connectorsOrder, appearance.collapsePasskeyButton, config.walletConnectors]);

  useEffect(() => {
    const connectorType = connectorsOrder.find((item) => item === ConnectorType.Email || item === ConnectorType.Phone);
    if (connectorType) {
      setToggleEmailOrPhone(connectorType);
    }
  }, [connectorsOrder]);

  const connectorComponents = useMemo(() => {
    if (appearance?.splitEmailAndPhone) {
      return {
        email: <EmailInput />,
        phone: <PhoneLogin />,
        social: <SocialLogin />,
        wallet: <Wallets />,
        passkey: <PasskeyButtons />,
      };
    }
    const emailOrPhone = <>{toggleEmailOrPhone === ConnectorType.Email ? <EmailInput /> : <PhoneLogin />}</>;
    return {
      email: emailOrPhone,
      phone: emailOrPhone,
      social: <SocialLogin />,
      wallet: <Wallets />,
      passkey: <PasskeyButtons />,
    };
  }, [appearance?.splitEmailAndPhone, toggleEmailOrPhone]);

  const switchEmailOrPhoneComponent = (
    <SwitchEmailOrPhone toggleEmailOrPhone={toggleEmailOrPhone} setToggleEmailOrPhone={setToggleEmailOrPhone} />
  );

  const content = useMemo(() => {
    const emailIndex = connectorsOrder.indexOf(ConnectorType.Email);
    const phoneIndex = connectorsOrder.indexOf(ConnectorType.Phone);
    const collapseEmailAndPhone = !appearance?.splitEmailAndPhone && emailIndex != -1 && phoneIndex != -1;

    const emailOrPhone = collapseEmailAndPhone && emailIndex < phoneIndex ? ConnectorType.Phone : ConnectorType.Email;
    const showSwitchConnector =
      collapseEmailAndPhone && emailIndex < phoneIndex ? ConnectorType.Email : ConnectorType.Phone;
    const emailOrPhoneIndex = collapseEmailAndPhone && emailIndex < phoneIndex ? phoneIndex : emailIndex;

    return connectorsOrder.map((connector, index) => {
      if (collapseEmailAndPhone && emailOrPhone === connector) {
        return undefined;
      }
      const component = connectorComponents[connector];
      const dividerStartIndex = collapseEmailAndPhone && emailOrPhoneIndex === 0 ? 1 : 0;
      return (
        <React.Fragment key={`${connector}-${index}`}>
          {index > dividerStartIndex && connectorsOrder.length !== 1 && <Divider>{locales.or}</Divider>}
          {component}
          {collapseEmailAndPhone && showSwitchConnector === connector && switchEmailOrPhoneComponent}
        </React.Fragment>
      );
    });
  }, [connectorComponents, connectorsOrder, appearance?.splitEmailAndPhone, switchEmailOrPhoneComponent]);

  const logoUrl = useMemo(() => {
    if (!appearance?.logo) {
      return logo;
    }
    if (typeof appearance?.logo === 'string') {
      return appearance?.logo;
    }

    if (currentMode) {
      return appearance.logo[currentMode];
    }
    return;
  }, [currentMode, appearance.logo]);

  return (
    <PageContent>
      {appearance?.logo !== false && <Logo src={logoUrl}></Logo>}

      {content}
    </PageContent>
  );
};

export default Signup;
